import { Link } from "react-router-dom";
import { ArrowRight, BellRing, ShieldCheck } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Motion";
import PageSeo from "@/components/PageSeo";
import Faq from "@/components/Faq";
import PriceEstimator from "@/components/PriceEstimator";
import { TRADES } from "@/data/content";
import { TARIFS, TARIFS_YEAR, formatRange, formatMonthlyRange } from "@/data/tarifs";
import { openCallbackModal, trackEvent } from "@/lib/analytics";
import { hy } from "@/lib/hyphenate";

const TARIF_DISCLAIMER =
  "Ces montants sont donnés à titre indicatif (repère indicatif communiqué par le cabinet, primes annuelles). La prime réelle dépend notamment du chiffre d'affaires, de l'expérience, des activités effectivement déclarées, des antécédents d'assurance et de sinistres, des franchises, de l'effectif, de la zone d'intervention et des critères propres à l'assureur.";

const FAQ_PRIX = [
  {
    q: "Combien coûte une assurance décennale par mois ?",
    a: "D’après les repères communiqués par le cabinet, un contrat RC Pro + décennale représente de 1 000 à 7 000 € par an selon le métier, soit environ 83 à 583 € par mois : environ 83 à 208 € par mois pour un peintre, 250 à 417 € pour un maçon, 433 à 583 € pour un fumiste. La possibilité de payer mensuellement et son coût éventuel dépendent de l’assureur.",
  },
  {
    q: "Existe-t-il un prix moyen pour une assurance décennale ?",
    a: "Non : il n'existe pas de tarif universel. Les fourchettes présentées sur cette page sont des repères indicatifs communiqués par le cabinet, jamais des tarifs garantis. Seule l'étude de votre dossier par un assureur donne un chiffre fiable.",
  },
  {
    q: "Une entreprise en création paie-t-elle moins cher ?",
    a: "La prime est calculée sur le chiffre d'affaires prévisionnel, souvent plus faible au démarrage — mais l'expérience et les diplômes du dirigeant pèsent davantage. Le tarif reste déterminé dossier par dossier.",
  },
  {
    q: "Une résiliation par mon ancien assureur est-elle rédhibitoire ?",
    a: "Elle peut modifier les conditions d'acceptation et le tarif. Précisez le motif et la date de résiliation, puis fournissez les pièces demandées. Une étude du dossier reste nécessaire ; elle ne garantit pas une proposition. Des déclarations inexactes peuvent avoir des conséquences sur votre contrat et votre indemnisation.",
  },
  {
    q: "Les fourchettes du tableau sont-elles garanties ?",
    a: "Non. Ce sont des repères indicatifs communiqués par le cabinet. La prime réelle dépend notamment du chiffre d'affaires, de l'expérience, des activités déclarées, des antécédents, des franchises, de l'effectif, de la zone d'intervention et des critères de l'assureur.",
  },
  {
    q: "Comment obtenir mon tarif réel ?",
    a: "Décrivez votre activité dans le formulaire de demande de devis. Un conseiller vous contacte pour préciser vos besoins et les pièces nécessaires. Le tarif peut être établi après étude du dossier et acceptation par l'assureur ; la demande est gratuite et sans engagement.",
  },
  {
    q: "Comment trouver une assurance décennale pas chère ?",
    a: "Comparez des offres portant sur les mêmes activités et techniques, avec un chiffre d'affaires exact. Rapprochez la cotisation annuelle taxes comprises et les frais éventuels, puis examinez séparément les franchises. Une mensualité faible ne suffit pas à prouver une économie. Les justificatifs d'expérience aident à faire étudier votre profil, sans garantir une réduction.",
  },
  {
    q: "Quelle est la meilleure assurance décennale pour un artisan ?",
    a: "Il n'existe pas de meilleur assureur pour tous les métiers. Le contrat adapté doit correspondre à vos travaux réels, aux techniques acceptées, à vos chantiers et à votre trésorerie. Comparez les exclusions, franchises, garanties complémentaires, dates d'effet et modalités de gestion des sinistres avant de départager les prix.",
  },
];

const PRIX_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_PRIX.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const SECTIONS = [
  {
    h: "Pourquoi le prix varie selon le métier ?",
    paras: [
      "Chaque métier du BTP présente une sinistralité propre : un fumiste dont le défaut de conduit peut provoquer un incendie, ou une entreprise de VRD dont l'affaissement de chaussée coûte des dizaines de milliers d'euros à reprendre, ne présentent pas le même risque qu'un peintre ou un agenceur. Les assureurs tarifient la gravité potentielle des désordres (solidité de l'ouvrage, impropriété à destination) et leur fréquence constatée par activité.",
    ],
  },
  {
    h: "Influence du chiffre d'affaires",
    paras: [
      "Le chiffre d'affaires réalisé ou prévisionnel fait partie des critères de tarification. Vérifiez dans chaque proposition la base de calcul, une éventuelle prime minimale et les modalités de régularisation. Ces conditions peuvent modifier le budget final même lorsque la cotisation annoncée au départ est faible.",
    ],
  },
  {
    h: "Influence de l'expérience professionnelle",
    paras: [
      "Joignez vos justificatifs d'expérience, diplômes et certifications correspondant aux travaux déclarés. Ils permettent à l'assureur de mieux étudier vos compétences et votre dossier. Ils ne donnent pas droit automatiquement à une réduction de cotisation.",
    ],
  },
  {
    h: "Création d'entreprise",
    paras: [
      "En création, l'assureur étudie notamment l'expérience du dirigeant, ses diplômes, les travaux prévus et le chiffre d'affaires prévisionnel. Indiquez votre date de début d'activité dans la demande. L'acceptation et le tarif dépendent de l'étude du dossier.",
    ],
  },
  {
    h: "Antécédents et résiliation",
    paras: [
      "Les sinistres, une résiliation ou une interruption d'assurance peuvent peser sur l'acceptation du dossier et le tarif. Décrivez votre situation exacte et fournissez votre relevé d'informations lorsqu'il est demandé. Les conditions proposées dépendent notamment des motifs et des dates de ces événements.",
    ],
  },
  {
    h: "Franchise et niveau de garantie",
    paras: [
      "Une franchise plus élevée peut réduire la cotisation si l'assureur propose ce choix, mais augmente le montant susceptible de rester à votre charge en cas de sinistre. Comparez les franchises garantie par garantie, ainsi que les plafonds et exclusions des garanties complémentaires. Une franchise n'est pas une dépense annuelle certaine : gardez-la distincte du budget de cotisation.",
    ],
  },
  {
    h: "Décennale pas chère : comment comparer le coût réel ?",
    paras: [
      "Faites comparer les offres sur la même liste de travaux, le même chiffre d'affaires et les mêmes techniques. Demandez la cotisation annuelle taxes comprises, les frais de dossier ou de courtage éventuels et le coût du paiement fractionné, en vérifiant ce qui est déjà inclus. Conservez toutes vos activités réelles dans le dossier. Un prix inférieur obtenu en retirant des travaux effectivement réalisés ne constitue pas une économie comparable.",
    ],
  },
  {
    h: "Meilleure décennale : choisir selon votre métier",
    paras: [
      "Commencez par la concordance entre vos travaux et les activités acceptées dans le contrat. Un maçon doit notamment faire préciser ses interventions structurelles ; un électricien qui pose des bornes IRVE doit faire étudier cette activité ; un couvreur doit distinguer couverture et étanchéité selon ses chantiers. Les fiches métiers détaillent ces points, avec des conseils de budget et les pièges d'une offre apparemment moins chère. Il ne s'agit pas d'un classement d'assureurs.",
    ],
  },
  {
    h: "Comment obtenir son tarif réel ?",
    paras: [
      "Renseignez votre activité, votre chiffre d'affaires, votre expérience, votre effectif et votre situation d'assurance. Un conseiller vous contacte pour compléter l'étude. La demande est gratuite et sans engagement ; une proposition dépend de l'étude et de l'acceptation du dossier par l'assureur.",
    ],
  },
];

export default function Prix() {
  return (
    <div data-testid="prix-page">
      <PageSeo path="/prix" jsonLd={PRIX_FAQ_LD} />
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-24">
        <Reveal y={12}>
          <SectionTag className="mb-4">Prix & tarifs — édition 2026</SectionTag>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tighter leading-[0.92] mb-6">
            {hy("Prix assurance décennale artisan")} : <span className="text-accent">tarifs par métier</span> en 2026
          </h1>
          <p className="max-w-2xl text-base sm:text-lg leading-relaxed text-ink/80 mb-10">
            Il n'existe pas de tarif universel pour l'assurance décennale : chaque prime est calculée
            sur mesure. En revanche, il est possible de donner des <strong>fourchettes indicatives communiquées par le cabinet</strong> par métier — par an et par mois — jamais présentées comme des tarifs garantis.
          </p>
        </Reveal>

        <section aria-labelledby="estimateur-title" className="mb-16">
          <h2 id="estimateur-title" className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-6">Estimez le prix de votre décennale</h2>
          <PriceEstimator />
        </section>

        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-4">Tarifs de l’assurance décennale par métier ({TARIFS_YEAR})</h2>
        <p className="max-w-3xl text-base leading-relaxed text-ink/75 mb-8">Vous recherchez une décennale pas chère ou le meilleur contrat pour votre métier ? Chaque fiche ci-dessous explique comment comparer les devis, quels travaux faire confirmer et quelles économies examiner sans retirer d’activités réellement exercées.</p>

        <Reveal className="mb-4">
          <div className="overflow-x-auto border-2 border-ink bg-white shadow-brutal" data-testid="prix-table">
            <table className="w-full min-w-[720px] text-left">
              <caption className="sr-only">Prix indicatif de l’assurance RC Pro + décennale par métier, par an et par mois</caption>
              <thead>
                <tr className="border-b-2 border-ink bg-ink text-paper">
                  <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em]">Métier</th>
                  <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em]">Prix indicatif par an</th>
                  <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em]">Équivalent par mois</th>
                  <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em]">Page métier</th>
                </tr>
              </thead>
              <tbody>
                {TRADES.map((t, i) => (
                  <tr key={t.slug} className={`border-b border-ink/10 ${i % 2 === 1 ? "bg-smoke/40" : ""}`}>
                    <td className="px-5 py-3.5 font-semibold">{t.name}</td>
                    <td className="px-5 py-3.5 font-mono text-sm" data-testid={`prix-tarif-${t.slug}`}>
                      {formatRange(TARIFS[t.slug])}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-sm text-ink/75">
                      ≈ {formatMonthlyRange(TARIFS[t.slug])}
                    </td>
                    <td className="px-5 py-3.5">
                      <a
                        href={`/metiers/${t.slug}#decennale-pas-chere`}
                        className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-accent hover:underline underline-offset-4"
                      >
                        Décennale {t.name.toLowerCase()}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <p className="mb-10 text-xs leading-relaxed text-ink/60 max-w-3xl" data-testid="prix-disclaimer">
          {TARIF_DISCLAIMER}
        </p>

        <Reveal className="mb-16 flex flex-wrap gap-4">
          <Link
            to="/devis"
            onClick={() => trackEvent("price_page_cta")}
            data-testid="prix-cta-calculer"
            className="inline-flex items-center gap-3 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper shadow-brutal transition-all hover:bg-ink hover:shadow-none"
          >
            Demander mon tarif
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            onClick={openCallbackModal}
            data-testid="prix-cta-callback"
            className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-paper"
          >
            <BellRing className="h-4 w-4" />
            Être rappelé
          </button>
        </Reveal>

        <div className="mb-16">
          {SECTIONS.map((s, i) => (
            <Reveal key={i}>
              <div className="grid gap-4 border-t-2 border-ink py-10 sm:grid-cols-12 sm:gap-8 items-start">
                <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-accent sm:col-span-2">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <div className="sm:col-span-10">
                  <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-4">{s.h}</h2>
                  {s.paras.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-ink/75 max-w-3xl">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mb-16">
          <SectionTag className="mb-4">Questions fréquentes</SectionTag>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tighter mb-8">
            FAQ prix assurance décennale
          </h2>
          <Faq items={FAQ_PRIX} prefix="prix-faq" />
        </Reveal>

        <Reveal className="flex items-start gap-4 border border-ink/15 bg-smoke/40 p-8">
          <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed text-ink/75">
            <strong>Bon à savoir :</strong> ces travaux sont susceptibles d'engager la responsabilité
            décennale lorsque les conditions légales sont réunies, notamment lorsque le désordre compromet
            la solidité de l'ouvrage ou le rend impropre à sa destination. Pour comprendre le cadre légal,
            consultez notre guide{" "}
            <Link to="/assurance-decennale" className="underline underline-offset-4 hover:text-accent">
              sur l'assurance décennale
            </Link>{" "}
            et la page{" "}
            <Link to="/attestation" className="underline underline-offset-4 hover:text-accent">
              attestation décennale
            </Link>
            .
          </p>
        </Reveal>
      </div>

      <section className="bg-accent text-paper">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-20">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter leading-[0.92]">
              Demandez un tarif<br />pour vos travaux.
            </h2>
            <p className="mt-4 max-w-xl text-base text-paper">
              Gratuit et sans engagement : un conseiller spécialiste BTP vous répond rapidement.
            </p>
            <Link
              to="/devis"
              onClick={() => trackEvent("price_page_cta")}
              data-testid="prix-band-cta"
              className="mt-10 inline-flex items-center gap-3 border-2 border-night bg-night px-10 py-5 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night"
            >
              Demander mon tarif
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
